import { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar as CalendarIcon, Trash2, CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TeamMeditation } from '@/lib/hooks/use-team-meditations';
import { downloadIcs } from '@/lib/utils';

interface TeamMeditationCalendarProps {
  meditations: TeamMeditation[];
  activeMeditationId?: string | null;
  activeMeditationIds?: string[];
  onDelete?: (id: string) => void;
  onOpenActive?: (id: string) => void;
  title?: string;
  variant?: 'team' | 'samen';
}

export function TeamMeditationCalendar({
  meditations,
  activeMeditationId,
  activeMeditationIds = [],
  onDelete,
  onOpenActive,
  title = 'Team Meditatie Kalender',
  variant = 'team',
}: TeamMeditationCalendarProps) {
  const isActive = (id: string) =>
    activeMeditationIds.includes(id) || id === activeMeditationId;
  const [date, setDate] = useState<Date | undefined>(new Date());

  const meditationDates = meditations.map((m) => new Date(m.scheduled_at));

  const isMeditationDate = (d: Date) => {
    return meditationDates.some(
      (md) =>
        md.getDate() === d.getDate() &&
        md.getMonth() === d.getMonth() &&
        md.getFullYear() === d.getFullYear(),
    );
  };

  const getMeditationsForDate = (selectedDate: Date) => {
    return meditations.filter((m) => {
      const md = new Date(m.scheduled_at);
      return (
        md.getDate() === selectedDate.getDate() &&
        md.getMonth() === selectedDate.getMonth() &&
        md.getFullYear() === selectedDate.getFullYear()
      );
    });
  };

  const selectedDateMeditations = date ? getMeditationsForDate(date) : [];

  return (
    <Card className="bg-white shadow-md border-green-500 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-green-500/10 to-blue-500/10 pb-2">
        <CardTitle className="flex items-center text-lg font-medium text-green-800">
          <CalendarIcon className="h-5 w-5 mr-2 text-green-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          className="rounded-md border"
          modifiers={{
            meditation: (d) => isMeditationDate(d),
          }}
          modifiersClassNames={{
            meditation: 'relative border-2 border-green-500 rounded-full',
          }}
        />

        {selectedDateMeditations.length > 0 && (
          <div className="mt-4 space-y-2">
            {selectedDateMeditations.map((m) => {
              const d = new Date(m.scheduled_at);
              const active = isActive(m.id);
              const openable = active && !!onOpenActive;
              return (
                <div
                  key={m.id}
                  role={openable ? 'button' : undefined}
                  tabIndex={openable ? 0 : undefined}
                  onClick={openable ? () => onOpenActive!(m.id) : undefined}
                  onKeyDown={
                    openable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOpenActive!(m.id);
                          }
                        }
                      : undefined
                  }
                  className={`p-3 rounded-lg shadow-sm flex items-center justify-between ${
                    active
                      ? 'bg-green-50 border-2 border-green-500'
                      : 'bg-white border border-muted'
                  } ${openable ? 'cursor-pointer hover:bg-green-100' : ''}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{m.meditation_title}</p>
                      {active && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-medium">
                          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                          Bezig
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {d.toLocaleDateString('nl-NL', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })}{' '}
                      om{' '}
                      {d.toLocaleTimeString('nl-NL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-green-600"
                      title="Voeg toe aan agenda"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadIcs(
                          m.meditation_title,
                          m.scheduled_at,
                          m.duration_minutes ?? 20,
                          { variant, activityType: (m as any).activity_type },
                        );
                      }}
                    >
                      <CalendarPlus className="h-4 w-4" />
                    </Button>
                    {onDelete && m.id !== activeMeditationId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(m.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {meditations.length === 0 && (
          <p className="mt-4 text-sm text-muted-foreground text-center">
            Nog geen activiteiten gepland.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
